import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  DataApiResponse,
  EditUserSkillListModel,
  SkillModel,
  SkillType,
  UserSkillModel,
} from '@digilize/shared/definitions/src';

interface SkillSearchParams {
  searchValue: string;
  start: number;
  length: number;
  draw?: number;
  id?: string;
  excludeIds?: string[];
  excludeSkillType?: SkillType[];
}

@Injectable({
  providedIn: 'root',
})
export class SkillsService {
  private readonly http = inject(HttpClient);

  getLanguageSkills = () =>
    this.http.get<DataApiResponse<UserSkillModel[]>>('/api/v1.0/my/skills', {
      params: { skill_type: SkillType.Language },
    });

  getListOfUserLanguages = (searchValue: string) =>
    this.http.get<DataApiResponse<SkillModel[]>>('/api/v1.0/my/skills/', {
      params: {
        'search.value': searchValue,
        skill_type: SkillType.Language,
      },
    });

  getListOfPossibleLanguages = (searchValue: string) =>
    this.http.get<DataApiResponse<SkillModel[]>>('/api/v1.0/skills/', {
      params: {
        'search.value': searchValue,
        skill_type: SkillType.Language,
      },
    });

  putLanguagesSkills = (req: EditUserSkillListModel) =>
    this.http.put<DataApiResponse<UserSkillModel[]>>('/api/v1.0/my/skills', {
      ...req,
      type: SkillType.Language,
    });

  getAvailableSoftSkills = (searchValue: string) =>
    this.http.get<DataApiResponse<SkillModel[]>>('/api/v1.0/my/skills/', {
      params: {
        'search.value': searchValue,
        skill_type: SkillType.Softskill,
        length: 25,
        draw: 25,
      },
    });

  getSoftSkills = () =>
    this.http.get<DataApiResponse<UserSkillModel[]>>('/api/v1.0/my/skills', {
      params: { skill_type: SkillType.Softskill },
    });

  putSoftSkills = (req: EditUserSkillListModel) =>
    this.http.put<DataApiResponse<UserSkillModel[]>>('/api/v1.0/my/skills', {
      ...req,
      type: SkillType.Softskill,
    });

  postSkillsRank = (req: UserSkillModel) => this.http.post<UserSkillModel>('/api/v1.0/my/skills/rank', req);

  getProfessionalSkillsByJob = (obj: SkillSearchParams) =>
    this.http.get<DataApiResponse<SkillModel[]>>('/api/v1.0/my/skills/', {
      params: {
        'search.value': obj.searchValue,
        skill_type: SkillType.Unknown,
        start: obj.start,
        length: obj.length,
        draw: obj.length,
        profession: obj.id,
      },
    });

  getProfessionalSkillsByEducation = (obj: SkillSearchParams) =>
    this.http.get<DataApiResponse<SkillModel[]>>('/api/v1.0/my/skills/', {
      params: {
        'search.value': obj.searchValue,
        skill_type: SkillType.Unknown,
        start: obj.start,
        length: obj.length,
        draw: obj.length,
        education: obj.id,
      },
    });

  getProfessionalSkills = () =>
    this.http.get<DataApiResponse<UserSkillModel[]>>('/api/v1.0/my/skills', {
      params: { skill_type: SkillType.Unknown },
    });

  getAvailableKnowledgeSkills = (searchValue: string) =>
    this.http.get<DataApiResponse<SkillModel[]>>('/api/v1.0/my/skills/', {
      params: {
        'search.value': searchValue,
        skill_type: SkillType.Knowledge,
        length: 25,
        draw: 25,
      },
    });

  getKnowledgeSkillsByJob = (obj: SkillSearchParams) =>
    this.http.get<DataApiResponse<SkillModel[]>>('/api/v1.0/my/skills/', {
      params: {
        'search.value': obj.searchValue,
        skill_type: SkillType.Knowledge,
        start: obj.start,
        length: obj.length,
        draw: obj.length,
        profession: obj.id,
      },
    });

  getKnowledgeSkillsByEducation = (obj: SkillSearchParams) =>
    this.http.get<DataApiResponse<SkillModel[]>>('/api/v1.0/my/skills/', {
      params: {
        'search.value': obj.searchValue,
        skill_type: SkillType.Knowledge,
        start: obj.start,
        length: obj.length,
        draw: obj.length,
        education: obj.id,
      },
    });

  getKnowledgeSkills = () =>
    this.http.get<DataApiResponse<UserSkillModel[]>>('/api/v1.0/my/skills', {
      params: { skill_type: SkillType.Knowledge },
    });

  putProfessionalSkills = (req: EditUserSkillListModel) =>
    this.http.put<DataApiResponse<UserSkillModel[]>>('/api/v1.0/my/skills', {
      ...req,
      type: SkillType.Unknown,
    });

  putSkills = (req: EditUserSkillListModel) =>
    this.http.put<DataApiResponse<UserSkillModel[]>>('/api/v1.0/my/skills', {
      ...req,
      type: SkillType.Skills,
    });

  putKnowledgeSkills = (req: EditUserSkillListModel) =>
    this.http.put<DataApiResponse<UserSkillModel[]>>('/api/v1.0/my/skills', {
      ...req,
      type: SkillType.Knowledge,
    });

  getTasksSkillsByJob = (obj: SkillSearchParams) =>
    this.http.get<DataApiResponse<SkillModel[]>>('/api/v1.0/my/skills/', {
      params: {
        'search.value': obj.searchValue,
        skill_type: SkillType.Unknown,
        start: obj.start,
        length: obj.length,
        draw: obj.length,
        profession: obj.id,
      },
    });

  getTasksSkillsByEducation = (obj: SkillSearchParams) =>
    this.http.get<DataApiResponse<SkillModel[]>>('/api/v1.0/my/skills/', {
      params: {
        'search.value': obj.searchValue,
        skill_type: SkillType.Unknown,
        start: obj.start,
        length: obj.length,
        draw: obj.length,
        education: obj.id,
      },
    });

  getTasksSkills = () =>
    this.http.get<DataApiResponse<UserSkillModel[]>>('/api/v1.0/my/skills', {
      params: { skill_type: SkillType.Unknown },
    });

  putTasksSkills = (req: EditUserSkillListModel) =>
    this.http.put<DataApiResponse<UserSkillModel[]>>('/api/v1.0/my/skills', req);

  getItSkillsByJob = (obj: SkillSearchParams) =>
    this.http.get<DataApiResponse<SkillModel[]>>('/api/v1.0/my/skills/', {
      params: {
        'search.value': obj.searchValue,
        skill_type: SkillType.Unknown,
        start: obj.start,
        length: obj.length,
        draw: obj.length,
        profession: obj.id,
      },
    });

  getItSkillsByEducation = (obj: SkillSearchParams) =>
    this.http.get<DataApiResponse<SkillModel[]>>('/api/v1.0/my/skills/', {
      params: {
        'search.value': obj.searchValue,
        skill_type: SkillType.Unknown,
        start: obj.start,
        length: obj.length,
        draw: obj.length,
        education: obj.id,
      },
    });

  searchForSkills = (obj: SkillSearchParams) => {
    let params = new HttpParams()
      .set('search.value', obj.searchValue)
      .set('start', obj.start)
      .set('length', obj.length)
      .set('draw', obj.length);

    if (obj.id) {
      params = params.set('profession', obj.id);
    }
    if (obj.excludeIds?.length) {
      obj.excludeIds.forEach((id) => (params = params.append('exclude_ids', id)));
    }
    if (obj.excludeSkillType?.length) {
      obj.excludeSkillType.forEach((type) => (params = params.append('exclude_skill_type', type)));
    }

    return this.http.get<DataApiResponse<SkillModel[]>>('/api/v1.0/skills/', { params });
  };

  putItSkills = (req: EditUserSkillListModel) =>
    this.http.put<DataApiResponse<UserSkillModel[]>>('/api/v1.0/my/skills', req);

  addUserSkill = (req: UserSkillModel) => this.http.post<UserSkillModel>('/api/v1.0/my/skills', req);

  getAllUserSkills = () => this.http.get<DataApiResponse<UserSkillModel[]>>('/api/v1.0/my/skills');

  /** @deprecated Use addUserSkill instead */
  addNewSkill = (req: unknown) => this.http.post('/api/v1.0/my/skills', req);

  getSkillsByProfession = (professionId: string, exclude_skill_type?: SkillType[], excludeIds?: string[]) => {
    const body: Record<string, unknown> = { profession: professionId };
    if (exclude_skill_type?.length) body.exclude_skill_type = exclude_skill_type;
    if (excludeIds?.length) body.exclude_ids = excludeIds;
    return this.http.post<SkillModel[]>('/api/v1.0/skills/by-profession', body);
  };

  deleteUserSkill = (id: string) => this.http.delete(`/api/v1.0/my/skills/${id}`);
}
